import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import FunnelStageLabel from './FunnelStageLabel';
import { UI, cardSx, stageDefinitions, stepDefinitions, value } from './operationalUi';

export interface StageCount {
  below: number;
  inLine: number;
  above: number;
  n: number;
  tooEarly?: number;
  stepValue?: number | null;
}
export default function StageCountBars({ stages, market }: { stages: Record<string, StageCount>; market: string }) {
  return (
    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', gap: 0.35, p: 1.5, height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: UI.teal, fontSize: 13, fontWeight: 600, letterSpacing: 0.6 }}>
          PORTFOLIO FUNNEL
        </Typography>
        <Typography sx={{ fontSize: 12, color: UI.muted }}>Campaigns v their own peers</Typography>
      </Stack>
      {stageDefinitions.map((stage, i) => {
        const row = stages[stage.id];
        const parts = [row?.below || 0, row?.inLine || 0, row?.above || 0],
          n = row?.n || 0;
        return (
          <React.Fragment key={stage.id}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px minmax(0,1fr)',
                gap: 1,
                alignItems: 'center',
                flex: 1,
              }}
            >
              <FunnelStageLabel step={i + 1} label={stage.label}>
                <Typography sx={{ fontSize: 12, color: UI.muted, lineHeight: 1.15 }}>
                  {market && i === 0 ? 'Not available by market' : `${n} compared`}
                </Typography>
              </FunnelStageLabel>
              <Box
                role="img"
                aria-label={`${stage.label}: ${parts[0]} well below, ${parts[1]} in line, ${parts[2]} well above, ${n} compared`}
                sx={{ minWidth: 0 }}
              >
                {n ? (
                  <>
                    <Box sx={{ display: 'flex', height: 16, borderRadius: 0.6, overflow: 'hidden' }}>
                      {parts.map(
                        (count, index) =>
                          count > 0 && (
                            <Box
                              key={index}
                              sx={{
                                bgcolor: (index === 0 ? UI.below : index === 1 ? UI.in_line : UI.above).background,
                                flex: count,
                                minWidth: 0,
                              }}
                            />
                          ),
                      )}
                    </Box>
                    <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                      {parts.map((count, index) => (
                        <Typography
                          key={index}
                          sx={{
                            fontSize: 12,
                            color: (index === 0 ? UI.below : index === 1 ? UI.in_line : UI.above).color,
                          }}
                        >
                          {count} {['below', 'in line', 'above'][index]}
                        </Typography>
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Box
                    sx={{
                      ...UI.unscored,
                      width: '100%',
                      borderRadius: 0.6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                    }}
                  >
                    {market && i === 0 ? 'Not available by market' : 'No comparable campaigns'}
                  </Box>
                )}
              </Box>
            </Box>
            {i < 3 && (
              <Typography sx={{ textAlign: 'right', fontSize: 12, color: UI.muted, flexShrink: 0, lineHeight: 1.25 }}>
                {stepDefinitions[i].label} {value(row?.stepValue, stepDefinitions[i].unit)}
              </Typography>
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}
